import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Inbox, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { fetchEmailQueue, retryEmailNotification } from '@/hooks/useAdmin';
import type { EmailQueueItem } from '@uniticket/shared';

const STATUS_VALUES = ['PENDING', 'SENT', 'FAILED', 'SKIPPED'] as const;
const EVENT_TYPE_VALUES = [
  'TICKET_CREATED',
  'STATUS_CHANGED',
  'COMMENT_ADDED',
  'SLA_WARNING',
  'SLA_BREACH',
  'TICKET_ESCALATED',
  'TICKET_RESOLVED',
] as const;

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  SENT: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  SKIPPED: 'bg-slate-100 text-slate-500',
};

export function EmailQueue() {
  const { t } = useTranslation(['admin', 'common']);
  const [items, setItems] = useState<EmailQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { limit: '50' };
      if (statusFilter) params.status = statusFilter;
      if (eventTypeFilter) params.eventType = eventTypeFilter;
      const res = await fetchEmailQueue(params);
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toast.error(t('admin:emailQueue.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, statusFilter, eventTypeFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleRetry(id: string) {
    try {
      await retryEmailNotification(id);
      toast.success(t('admin:emailQueue.retried'));
      loadData();
    } catch {
      toast.error(t('admin:emailQueue.retryError'));
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand-gradient">
            <Inbox className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{t('admin:emailQueue.title')}</h1>
            <p className="text-sm text-slate-500">
              {t('admin:emailQueue.count', { count: total })}
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={loadData}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          {t('admin:emailQueue.refresh')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('admin:emailQueue.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin:emailQueue.allStatuses')}</SelectItem>
            {STATUS_VALUES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventTypeFilter} onValueChange={(v) => setEventTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder={t('admin:emailQueue.allEvents')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin:emailQueue.allEvents')}</SelectItem>
            {EVENT_TYPE_VALUES.map((et) => (
              <SelectItem key={et} value={et}>{t(`admin:emailTemplates.events.${et}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.ticket')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.recipient')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.event')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.status')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.retries')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.created')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.sent')}</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600">{t('admin:emailQueue.cols.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      {t('admin:emailQueue.empty')}
                    </td>
                  </tr>
                )}
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-mono text-xs">{item.ticketNumber}</td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]">{item.recipientEmail}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs">{t(`admin:emailTemplates.events.${item.eventType}`)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-500'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500">{item.retryCount}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(item.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {item.sentAt ? new Date(item.sentAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.status === 'FAILED' && (
                        <Button size="sm" variant="outline" onClick={() => handleRetry(item.id)}>
                          {t('admin:emailQueue.retry')}
                        </Button>
                      )}
                      {item.errorMessage && (
                        <span className="block text-[10px] text-red-500 mt-1 truncate max-w-[200px]" title={item.errorMessage}>
                          {item.errorMessage}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
